/**
 * linked_page_kind → routePaths キー / i18n ラベルの解決ロジック。
 * AdminWorkflowTemplates の SubStepEditor と WorkflowCaseDetail の SubStepRow が共通で使う。
 * page 横断のため src/models/ に配置する。
 */
import { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import { routePaths } from '~/routes/routePaths'

// ─── kind → route map ────────────────────────────────────────────────────────

/**
 * LinkedPageKind の各値に対応する routePaths のキー。
 * routeKey は routePaths の keyof として使用し、直接 string を書かない (dead link 防止)。
 */
type KindEntry = {
  routeKey: keyof typeof routePaths
  /**
   * true の kind は、resolveLinkedPage が非空 linkedPlanId を受け取ったとき
   * `${routePaths[routeKey].path}/${linkedPlanId}` で path segment を埋める
   * (workflow-substep-transition design: `/plan/procurement/:id` /
   * `/plan/gen-sales/:id` 詳細画面への遷移)。省略時は false 相当
   * (= linkedPlanId を無視して現行の一覧 route のまま)。
   */
  needsPlanId?: boolean
  /**
   * true の kind は、発電所別同時同量画面 (/balance/generation-detail) のように
   * クエリパラメータ `?bg=` で BG コードを受け取る。親 WorkflowCase の bg_code を
   * 引き継ぎ、未設定の場合は `?bg=` を付与しない (ページ側の noBgSelected フォールバック)。
   */
  needsBgCode?: boolean
}

/**
 * UNSPECIFIED を除く全 LinkedPageKind を必須キーとする exhaustive map。
 * proto に kind を追加した際にこの map への追加漏れをコンパイルエラーで検出できる。
 */
export const LINKED_PAGE_KIND_MAP: Record<
  Exclude<LinkedPageKind, LinkedPageKind.UNSPECIFIED>,
  KindEntry
> = {
  [LinkedPageKind.WORKFLOW_CASES]: {
    routeKey: 'workflowCases',
  },
  [LinkedPageKind.DEMAND_ACTUAL_STATISTICS]: {
    routeKey: 'demandActualStatistics',
  },
  [LinkedPageKind.SUPPLY_GAP]: {
    routeKey: 'supplyGap',
  },
  [LinkedPageKind.SUPPLY_BILATERAL]: {
    routeKey: 'supplyBilateral',
  },
  [LinkedPageKind.ADMIN_NOTIFICATION_CHANGE]: {
    routeKey: 'adminNotificationChange',
  },
  [LinkedPageKind.SUPPLY_PLANT]: {
    routeKey: 'supplyPlant',
  },
  [LinkedPageKind.SUPPLY_48DATA]: {
    routeKey: 'supply48Data',
  },
  [LinkedPageKind.ADMIN_INDIRECT_TRANSMISSION]: {
    routeKey: 'adminIndirectTransmission',
  },
  [LinkedPageKind.SUPPLY_RENEW_FORECAST]: {
    routeKey: 'supplyRenewForecast',
  },
  [LinkedPageKind.ADMIN_FIP_PREMIUM]: {
    routeKey: 'adminFipPremium',
  },
  [LinkedPageKind.BIDDING_SPOT]: {
    routeKey: 'biddingSpot',
  },
  [LinkedPageKind.BIDDING_INTRADAY]: {
    routeKey: 'biddingIntraday',
  },
  [LinkedPageKind.ADMIN_BID_LIMITS]: {
    routeKey: 'adminBidLimits',
  },
  /**
   * @deprecated 選択肢 (`LINKED_PAGE_KIND_GROUPS`) からは除外済み。
   * 過去 tenant overlay data の href 解決互換のため map エントリのみ残置する
   * (`/plan/submission` 遷移を維持)。新規選択は `SUBMISSION_MANAGEMENT` を使う
   * (workflow-templates-linked-page-spec-compliance §4)。
   */
  [LinkedPageKind.PLAN_SUBMISSION]: {
    routeKey: 'planSubmission',
  },
  [LinkedPageKind.PLAN_PROCUREMENT]: {
    routeKey: 'planProcurement',
    needsPlanId: true,
  },
  [LinkedPageKind.PLAN_GEN_SALES]: {
    routeKey: 'planGenSales',
    needsPlanId: true,
  },
  [LinkedPageKind.PLAN_DEMAND_SUPPRESSION]: {
    routeKey: 'planDemandSuppression',
  },
  [LinkedPageKind.BALANCE_DEMAND]: {
    routeKey: 'balanceDemand',
  },
  [LinkedPageKind.BALANCE_GENERATION]: {
    routeKey: 'balanceGeneration',
  },
  [LinkedPageKind.REVENUE_FORECAST]: {
    routeKey: 'revenueForecast',
  },
  [LinkedPageKind.REVENUE_INVOICE]: {
    routeKey: 'revenueInvoice',
  },
  [LinkedPageKind.WEATHER_FORECAST]: {
    routeKey: 'weatherForecast',
  },
  // 断面はパスに含めない (計画断面は画面側で対象日から判定する、AC-DFD-000)
  [LinkedPageKind.DEMAND_FORECAST_DAILY]: {
    routeKey: 'demandForecastDaily',
  },
  // #7: 新 4 kind (DASHBOARD / CUSTOMER_CONTRACT / CUSTOMER_RATE / CUSTOMER_SCHEDULE)
  [LinkedPageKind.DASHBOARD]: {
    routeKey: 'home',
  },
  [LinkedPageKind.CUSTOMER_CONTRACT]: {
    routeKey: 'customerContract',
  },
  [LinkedPageKind.CUSTOMER_RATE]: {
    routeKey: 'customerRate',
  },
  [LinkedPageKind.CUSTOMER_SCHEDULE]: {
    routeKey: 'customerSchedule',
  },
  // 提出管理 (spec workflow-templates.md §機能詳細 4a)。 「計画提出」 メインカテゴリの
  // リンク先。 workflow-templates-linked-page-spec-compliance §5 で新設。
  [LinkedPageKind.SUBMISSION_MANAGEMENT]: {
    routeKey: 'submissionManagement',
  },

  // workflow-template-linked-page-8categories (2026-08-03 PdM 判断) で追加された
  // P0 リンク先。 旧 WEATHER_FORECAST は overlay data 互換のため map に残置するが、
  // 新規選択は ADMIN_WEATHER_FORECAST_POINTS を使う。
  [LinkedPageKind.DEMAND_SINGULAR_DAY]: {
    routeKey: 'demandSingularDay',
  },
  [LinkedPageKind.BALANCE_IMPUTATION_POLICY]: {
    routeKey: 'balanceImputationPolicy',
  },
  [LinkedPageKind.PLAN_SPLIT_SUPPLY]: {
    routeKey: 'planSplitSupply',
  },
  [LinkedPageKind.ADMIN_UPLOADS]: {
    routeKey: 'adminUploads',
  },
  [LinkedPageKind.ADMIN_OCCTO]: {
    routeKey: 'adminOccto',
  },
  // enum 値は wire 互換のため ADMIN_ESLJ_ACCOUNTS のまま。 画面自体は
  // 「JEPX会員情報管理」 に置き換わっており、 遷移先だけ新 route に向ける。
  [LinkedPageKind.ADMIN_ESLJ_ACCOUNTS]: {
    routeKey: 'adminJepxMemberInfo',
  },
  [LinkedPageKind.ADMIN_WEATHER_FORECAST_POINTS]: {
    routeKey: 'adminWeatherForecastPoints',
  },
  [LinkedPageKind.CUSTOMER_DEMAND_GROUPS]: {
    routeKey: 'adminDemandGroups',
  },
  [LinkedPageKind.ADMIN_APPROVAL_FLOW]: {
    routeKey: 'adminApprovalFlow',
  },
  [LinkedPageKind.BALANCE_GENERATION_DETAIL]: {
    routeKey: 'balanceGenerationDetail',
    needsBgCode: true,
  },
}

// ─── kind → ラベル key のみ解決 (= href 不要な表示用) ─────────────────────────

/**
 * LinkedPageKind から表示用ラベルの i18n key だけを解決する pure 関数。
 * caseId / section を持たない文脈 (= 雛形の閲覧画面など、 実際に遷移しない表示) で使う。
 *
 * - kind が UNSPECIFIED / undefined → null (= リンク先なし)
 * - それ以外 → routePaths[entry.routeKey].name (= t() で解決する i18n key)
 */
export function linkedPageLabelKey(
  kind: LinkedPageKind | undefined,
): string | null {
  if (kind === undefined || kind === LinkedPageKind.UNSPECIFIED) {
    return null
  }
  const entry: KindEntry | undefined = LINKED_PAGE_KIND_MAP[kind]
  if (entry === undefined) {
    return null
  }
  return routePaths[entry.routeKey].name
}

// ─── resolveLinkedPage ────────────────────────────────────────────────────────

/**
 * LinkedPageKind・caseId (と kind に応じて linkedPlanId・bgCode) から href と labelKey を同時に解決する pure 関数。
 * 2 つに分かれていた resolver (resolveLinkedPageHref / resolveLinkedPageRouteNameKey) を統合し、
 * 消費側 (SubStepRow) の dead branch を除去する。
 *
 * - kind が UNSPECIFIED / undefined → null (リンク非表示)
 * - それ以外 → { href: <base + ?workflowId= + id埋め>, labelKey: routePaths[entry.routeKey].name }
 *
 * needsPlanId な kind (PLAN_PROCUREMENT / PLAN_GEN_SALES) は、linkedPlanId が
 * 非空なら `${base}/${linkedPlanId}` で当該ワークフローに紐づいた計画作成
 * インスタンスの詳細 URL を組む (workflow-substep-transition design)。
 * linkedPlanId が未解決 (undefined / 空文字) の場合は現行どおり一覧 route の
 * まま — バックエンドが紐付く計画を解決できなかった時のフォールバック
 * (AC-02)。
 *
 * @param kind - SubStep の linked_page_kind
 * @param caseId - 親 WorkflowCase の ID
 * @param linkedPlanId - 解決済み plan_submission.id (= needsPlanId kind のみ使用。
 *   未解決時は undefined または空文字)
 * @param bgCode - 親 WorkflowCase の bg_code (= needsBgCode kind のみ使用。
 *   未設定時は undefined または空文字)
 */
export function resolveLinkedPage(
  kind: LinkedPageKind | undefined,
  caseId: string,
  linkedPlanId?: string,
  bgCode?: string,
): { href: string; labelKey: string } | null {
  if (kind === undefined || kind === LinkedPageKind.UNSPECIFIED) {
    return null
  }

  // early-return で UNSPECIFIED / undefined を除外済みのため、
  // kind は Exclude<LinkedPageKind, LinkedPageKind.UNSPECIFIED> に narrow されている。
  // exhaustive map から必ず entry が取れるが、将来 proto に追加された kind が
  // map に未追加の場合に備え防御的ガードを残す。
  const entry: KindEntry | undefined = LINKED_PAGE_KIND_MAP[kind]
  if (entry === undefined) {
    return null
  }

  // routePaths.path は as const literal 型なので string に明示変換してから操作する
  let base: string = routePaths[entry.routeKey].path

  if (
    entry.needsPlanId === true &&
    linkedPlanId !== undefined &&
    linkedPlanId !== ''
  ) {
    base = `${base}/${linkedPlanId}`
  }

  const queryParams = new URLSearchParams({ [WORKFLOW_ID_PARAM]: caseId })
  if (entry.needsBgCode === true && bgCode !== undefined && bgCode !== '') {
    queryParams.set('bg', bgCode)
  }

  return {
    href: `${base}?${queryParams.toString()}`,
    labelKey: routePaths[entry.routeKey].name,
  }
}
